import { calculateTotal } from '@calculations/price/utilities/total';
import { CalculationConfig } from '@calculations/types/calculation-config';
import { EditableSnapQuote } from '@snap-types/actions/edit-snap-quote';
import { Decimal } from '@snap-types/types/decimal';

import { QuotePriceType } from '../../types/state/quote-price';

export function transformSnapQuotePriceToStoreState(
  quotePrice: EditableSnapQuote['latestVersion']['snapQuoteLines'][number]['snapQuotePrices'][number],
  { decimalPlaces }: CalculationConfig,
): { uuid: string; quotePrice: QuotePriceType; totalPrice: Decimal } {
  const totalPrice = calculateTotal(
    quotePrice.quantity,
    quotePrice.unitPrice,
  ).toDecimalPlaces(decimalPlaces);

  return {
    uuid: quotePrice.uuid,
    totalPrice,
    quotePrice: {
      uuid: quotePrice.uuid,
      suggestedPriceStatus: quotePrice.suggestedPriceStatus,
      unitCost: quotePrice.unitCost?.toDecimalPlaces(decimalPlaces) ?? null,
      unitBurdenCost:
        quotePrice.unitBurdenCost?.toDecimalPlaces(decimalPlaces) ?? null,
      unitLaborCost:
        quotePrice.unitLaborCost?.toDecimalPlaces(decimalPlaces) ?? null,
      unitMaterialCost:
        quotePrice.unitMaterialCost?.toDecimalPlaces(decimalPlaces) ?? null,
      unitServiceCost:
        quotePrice.unitServiceCost?.toDecimalPlaces(decimalPlaces) ?? null,
      unitVariableCosts:
        quotePrice.unitVariableCosts?.toDecimalPlaces(decimalPlaces) ?? null,
      totalPrice,

      // Quantity
      automatedQuantity: quotePrice.automatedQuantity ?? null,
      quantity: quotePrice.quantity,
      userQuantity: quotePrice.userQuantity ?? null,

      // Unit Price
      automatedUnitPrice: quotePrice.automatedUnitPrice ?? null,
      calculatedUnitPrice: quotePrice.calculatedUnitPrice ?? null,
      unitPrice: quotePrice.unitPrice.toDecimalPlaces(decimalPlaces),
      userUnitPrice: quotePrice.userUnitPrice ?? null,

      // Unit Cost
      userUnitCost:
        quotePrice.userUnitCost?.toDecimalPlaces(decimalPlaces) ?? null,

      // Gross Margin
      automatedGrossMargin: quotePrice.automatedGrossMargin ?? null,
      calculatedGrossMargin: quotePrice.calculatedGrossMargin ?? null,
      grossMargin: quotePrice.grossMargin ?? null,
      userGrossMargin: quotePrice.userGrossMargin ?? null,

      // Contribution Margin
      automatedContributionMargin:
        quotePrice.automatedContributionMargin ?? null,

      calculatedContributionMargin:
        quotePrice.calculatedContributionMargin ?? null,
      contributionMargin: quotePrice.contributionMargin ?? null,
      userContributionMargin: quotePrice.userContributionMargin ?? null,

      purchaseMaterialAmount:
        quotePrice.purchaseMaterialAmount?.toDecimalPlaces(decimalPlaces) ??
        null,
      purchaseMaterialPercent:
        quotePrice.purchaseMaterialPercent?.toDecimalPlaces(decimalPlaces) ??
        null,
      fabricatedMaterialAmount:
        quotePrice.fabricatedMaterialAmount?.toDecimalPlaces(decimalPlaces) ??
        null,
      fabricatedMaterialPercent:
        quotePrice.fabricatedMaterialPercent?.toDecimalPlaces(decimalPlaces) ??
        null,
      laborCostAmount:
        quotePrice.laborCostAmount?.toDecimalPlaces(decimalPlaces) ?? null,
      laborCostPercent:
        quotePrice.laborCostPercent?.toDecimalPlaces(decimalPlaces) ?? null,
      burdenCostAmount:
        quotePrice.burdenCostAmount?.toDecimalPlaces(decimalPlaces) ?? null,
      burdenCostPercent:
        quotePrice.burdenCostPercent?.toDecimalPlaces(decimalPlaces) ?? null,
      serviceCostAmount:
        quotePrice.serviceCostAmount?.toDecimalPlaces(decimalPlaces) ?? null,
      serviceCostPercent:
        quotePrice.serviceCostPercent?.toDecimalPlaces(decimalPlaces) ?? null,
      status: 'ready',
      priceStrategy: quotePrice.priceStrategy,
      discountCode: quotePrice.discountCode ?? null,

      // Base Gross Margin:
      automatedBaseGrossMargin: quotePrice.automatedBaseGrossMargin ?? null,
      baseGrossMargin: quotePrice.baseGrossMargin ?? null,
      calculatedBaseGrossMargin: quotePrice.calculatedBaseGrossMargin ?? null,

      priceAdjustmentAmount: quotePrice.priceAdjustmentAmount ?? null,
      baseUnitPrice: quotePrice.baseUnitPrice ?? null,

      unitPriceAddition: quotePrice.unitPriceAddition ?? null,
      changesUnitPrice: quotePrice.changesUnitPrice ?? false,
    },
  };
}
